<template>
  <div class="security-categories">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold">Security Categories</h1>
    </div>

    <DataTable
      :columns="columns"
      :data="securityCategories"
      @rowClick="toggleCategory"
      :loading="isLoading"
      :error="error"
      :pagination="pagination"
      @page-change="handlePageChange"
      @sort-change="handleSortChange"
      @search-change="handleSearchChange"
    >
      <template #cell-status="{ item }">
        <div class="flex items-center space-x-2">
          <span 
            :class="[
              'px-2 py-1 rounded-full text-xs font-medium',
              hasEnabledSubcategories(item) 
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
            ]"
          >
            {{ hasEnabledSubcategories(item) ? 'Active' : 'Inactive' }}
          </span>
          <span class="text-sm text-text-muted dark:text-text-muted-dark">
            ({{ getEnabledSubcategoriesCount(item) }} enabled)
          </span>
        </div>
      </template>
    </DataTable>

    <!-- Expanded Category Panel -->
    <div v-if="selectedCategory" class="mt-8 bg-background-primary dark:bg-background-primary-dark rounded-lg border border-border dark:border-border-dark shadow-lg">
      <div class="p-4 border-b border-border dark:border-border-dark">
        <div class="flex justify-between items-start">
          <div>
            <div class="flex items-center space-x-3">
              <h2 class="text-xl font-bold text-text-primary dark:text-text-primary-dark">{{ selectedCategory.name }}</h2>
              <span 
                :class="[
                  'px-2 py-1 rounded-full text-xs font-medium',
                  hasEnabledSubcategories(selectedCategory) 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                ]"
              >
                {{ hasEnabledSubcategories(selectedCategory) ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <p class="mt-2 text-text-secondary dark:text-text-secondary-dark">{{ selectedCategory.description }}</p>
          </div>
          <button 
            @click="selectedCategory = null"
            class="p-2 text-text-muted dark:text-text-muted-dark hover:text-text-primary dark:hover:text-text-primary-dark transition-colors duration-200"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Subcategories Grid -->
      <div v-if="selectedCategory.sub_categories?.length" class="p-4">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div 
            v-for="subCategory in selectedCategory.sub_categories" 
            :key="subCategory.id"
            class="p-4 bg-background-secondary dark:bg-background-secondary-dark rounded-lg border border-border dark:border-border-dark hover:border-primary dark:hover:border-primary-light transition-colors duration-200"
          >
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <div class="flex items-center space-x-2 mb-2">
                  <h3 class="font-semibold text-text-primary dark:text-text-primary-dark">{{ subCategory.name }}</h3>
                  <span 
                    :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      subCategory.is_enabled 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    ]"
                  >
                    {{ subCategory.is_enabled ? 'Enabled' : 'Disabled' }}
                  </span>
                </div>
                <p class="text-sm text-text-secondary dark:text-text-secondary-dark">{{ subCategory.description }}</p>
                <p v-if="subCategory.content" class="mt-2 text-sm text-text-secondary dark:text-text-secondary-dark line-clamp-2">
                  {{ subCategory.content }}
                </p>
              </div>
              <div class="flex space-x-2">
                <button
                  v-if="subCategory.is_enabled"
                  @click.stop.prevent="openEditModal(selectedCategory, subCategory)"
                  class="p-1 text-text-muted hover:text-primary dark:text-text-muted-dark dark:hover:text-primary-light transition-colors duration-200"
                  title="Edit content"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  @click.stop.prevent="toggleSubCategoryStatus(selectedCategory, subCategory)"
                  class="p-1 text-text-muted hover:text-primary dark:text-text-muted-dark dark:hover:text-primary-light transition-colors duration-200"
                  :title="subCategory.is_enabled ? 'Disable subcategory' : 'Enable subcategory'"
                >
                  <svg v-if="subCategory.is_enabled" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                  <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="p-4 text-text-secondary dark:text-text-secondary-dark">
        No subcategories available for this category.
      </div>
    </div>

    <!-- Edit Content Modal -->
    <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
        <div class="p-6">
          <div class="flex justify-between items-start mb-6">
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
              Edit Content
            </h2>
            <button 
              @click="closeEditModal" 
              class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
            >
              <span class="sr-only">Close</span>
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form @submit.prevent="handleContentSubmit" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Content</label>
              <textarea
                v-model="editForm.content"
                rows="6"
                class="input mt-1"
                placeholder="Enter content for this security category..."
              ></textarea>
            </div>

            <div class="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                @click="closeEditModal"
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import DataTable from '@/components/DataTable.vue'

export default {
  name: 'SecurityCategories',
  components: {
    DataTable
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const selectedCategory = ref(null)
    const showEditModal = ref(false)
    const editingSubCategory = ref(null)
    const editForm = ref({
      content: ''
    })
    const pagination = ref({
      currentPage: 1,
      totalPages: 1,
      entriesPerPage: 50,
      totalEntries: 0,
      orderBy: 'name',
      sortDirection: 'asc',
      search: ''
    })

    // Computed properties
    const securityCategories = computed(() => store.state.securityCategories.data)
    const isLoading = computed(() => store.state.securityCategories.loading)
    const error = computed(() => store.state.securityCategories.error)

    const columns = [
      { 
        key: 'name', 
        label: 'Name',
        sortable: true,
        class: 'font-medium'
      },
      { 
        key: 'description', 
        label: 'Description',
        sortable: true
      },
      { 
        key: 'sub_categories', 
        label: 'Subcategories',
        formatter: (value) => value?.length || 0,
        sortable: true
      },
      {
        key: 'status',
        label: 'Status',
        slot: 'status'
      }
    ]

    const hasEnabledSubcategories = (category) => {
      return category?.sub_categories?.some(sub => sub.is_enabled) || false
    }

    const getEnabledSubcategoriesCount = (category) => {
      return category?.sub_categories?.filter(sub => sub.is_enabled).length || 0
    }

    const toggleCategory = async (category) => {
      // Prevent scroll to top by maintaining scroll position
      const scrollPosition = window.scrollY
      selectedCategory.value = selectedCategory.value?.id === category.id ? null : category
      // Restore scroll position after DOM update
      await nextTick()
      window.scrollTo(0, scrollPosition)
    }

    const toggleSubCategoryStatus = async (category, subCategory) => {
      // Prevent scroll to top by maintaining scroll position
      const scrollPosition = window.scrollY
      
      try {
        const tenantId = route.params.tenantId
        await store.dispatch('securityCategories/toggleSubCategory', {
          tenantId,
          categoryId: category.id,
          subCategoryId: subCategory.id,
          enabled: !subCategory.is_enabled
        })
        
        // No need to re-fetch data since the store mutation already updates the state
        // The computed properties will automatically reflect the changes
        
        // Restore scroll position after state update
        await nextTick()
        window.scrollTo(0, scrollPosition)
      } catch (err) {
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: err.message || 'Failed to toggle subcategory status',
          duration: 3000
        })
      }
    }

    const fetchSecurityCategories = async () => {
      try {
        const tenantId = route.params.tenantId
        const response = await store.dispatch('securityCategories/fetchData', {
          tenantId,
          page: pagination.value.currentPage,
          entriesPerPage: pagination.value.entriesPerPage,
          search: pagination.value.search,
          orderBy: pagination.value.orderBy,
          sortDirection: pagination.value.sortDirection
        })
        
        pagination.value = {
          currentPage: response.currentPage || 1,
          totalPages: response.totalPages || 1,
          entriesPerPage: response.entriesPerPage || 10,
          totalEntries: response.totalEntries || 0,
          orderBy: response.orderBy || 'name',
          sortDirection: response.sortDirection || 'asc',
          search: response.search || ''
        }
      } catch (err) {
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: err.message || 'Failed to load security categories',
          duration: 3000
        })
      }
    }

    const handlePageChange = (page) => {
      pagination.value.currentPage = page
      fetchSecurityCategories()
    }

    const handleSortChange = ({ orderBy, sortDirection }) => {
      pagination.value.orderBy = orderBy
      pagination.value.sortDirection = sortDirection
      fetchSecurityCategories()
    }

    const handleSearchChange = (search) => {
      pagination.value.search = search
      pagination.value.currentPage = 1
      fetchSecurityCategories()
    }

    const openEditModal = (category, subCategory) => {
      editingSubCategory.value = subCategory
      editForm.value.content = subCategory.content || subCategory.mock_text || ''
      showEditModal.value = true
    }

    const closeEditModal = () => {
      showEditModal.value = false
      editingSubCategory.value = null
      editForm.value.content = ''
    }

    const handleContentSubmit = async () => {
      if (!editingSubCategory.value) return

      // Prevent scroll to top by maintaining scroll position
      const scrollPosition = window.scrollY

      try {
        const tenantId = route.params.tenantId
        await store.dispatch('securityCategories/toggleSubCategory', {
          tenantId,
          categoryId: selectedCategory.value.id,
          subCategoryId: editingSubCategory.value.id,
          enabled: true,
          content: editForm.value.content
        })

        store.dispatch('notification/showNotification', {
          type: 'success',
          message: 'Content updated successfully',
          duration: 3000
        })

        closeEditModal()
        await fetchSecurityCategories()
        
        // Restore scroll position after state update
        await nextTick()
        window.scrollTo(0, scrollPosition)
      } catch (err) {
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: err.message || 'Failed to update content',
          duration: 3000
        })
      }
    }

    onMounted(() => {
      fetchSecurityCategories()
    })

    return {
      columns,
      selectedCategory,
      isLoading,
      error,
      pagination,
      hasEnabledSubcategories,
      getEnabledSubcategoriesCount,
      toggleCategory,
      toggleSubCategoryStatus,
      handlePageChange,
      handleSortChange,
      handleSearchChange,
      securityCategories,
      showEditModal,
      editingSubCategory,
      editForm,
      openEditModal,
      closeEditModal,
      handleContentSubmit
    }
  }
}
</script>

<style scoped>
.security-categories {
  @apply p-6;
}

.grid {
  display: grid;
  gap: 1rem;
}

@media (min-width: 768px) {
  .grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>